export class ApiError extends Error {
  status: number;
  body?: unknown;

  constructor(message: string, status: number, body?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }

  get isAuthError(): boolean {
    return this.status === 401;
  }

  get isTokenError(): boolean {
    return this.status === 401 && (
      this.message.includes('Token inválido') ||
      this.message.includes('Token expirado') ||
      this.message.includes('Token not found') ||
      this.message.includes('Unauthorized')
    );
  }
}

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:4001/api";

type FetchOptions = RequestInit & { revalidate?: number; token?: string };

// Helper function to get token from localStorage
function getStoredToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('authToken');
}

// Helper function to set token in both localStorage and cookies
function setTokenInStorage(token: string) {
  if (typeof window !== 'undefined') {
    localStorage.setItem('authToken', token);
    // Also set in cookie for middleware access
    document.cookie = `authToken=${token}; path=/; max-age=${7 * 24 * 60 * 60}; SameSite=Lax`;
  }
}

// Helper function to refresh token
async function refreshAuthToken(): Promise<string | null> {
  if (typeof window === 'undefined') return null;

  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) return null;

  try {
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken })
    });

    if (response.ok) {
      const data = await response.json();
      if (data.token) {
        // Use helper function to save in both localStorage and cookies
        setTokenInStorage(data.token);
        return data.token;
      }
    }
  } catch (error) {
    console.error('Erro ao renovar token:', error);
  }

  return null;
}

export async function apiFetch<T>(path: string, options: FetchOptions = {}): Promise<T> {
  const { token, ...fetchOptions } = options;
  const headers = new Headers(fetchOptions.headers ?? {});
  const body = fetchOptions.body as BodyInit | null | undefined;
  const isFormData = typeof FormData !== "undefined" && body instanceof FormData;

  if (!isFormData && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  // Use provided token or get from localStorage
  const authToken = token || getStoredToken();
  if (authToken) {
    headers.set("Authorization", `Bearer ${authToken}`);
    // console.log('🔑 Token sendo enviado:', authToken.substring(0, 20) + '...');
  } else {
    console.warn('⚠️ Nenhum token encontrado no localStorage');
  }

  const requestInit: RequestInit = {
    ...fetchOptions,
    credentials: fetchOptions.credentials ?? "include",
    headers,
    body
  };

  let response = await fetch(`${API_BASE_URL}${path}`, requestInit);

  // Verificar se o servidor enviou um novo token (renovação por atividade)
  if (response.ok && typeof window !== 'undefined') {
    const newToken = response.headers.get('X-New-Token');
    if (newToken) {
      // console.log('🔄 Token renovado automaticamente por atividade');
      setTokenInStorage(newToken);
      // Disparar evento para atualizar contextos
      window.dispatchEvent(new Event('auth:token-changed'));
    }
  }

  // If we get a 401 and have a refresh token, try to refresh
  if (response.status === 401 && !token && typeof window !== 'undefined') {
    const newToken = await refreshAuthToken();
    if (newToken) {
      // Retry with new token
      headers.set("Authorization", `Bearer ${newToken}`);
      const retryInit: RequestInit = { ...requestInit, headers };
      response = await fetch(`${API_BASE_URL}${path}`, retryInit);
    }
  }

  if (!response.ok) {
    let message = `Erro na requisição ${response.status}`;
    let parsedBody: unknown;

    try {
      parsedBody = await response.clone().json();
      if (typeof parsedBody === "string") {
        message = parsedBody;
      } else if (parsedBody && typeof parsedBody === "object" && "message" in parsedBody) {
        const bodyMessage = (parsedBody as { message?: unknown }).message;
        if (typeof bodyMessage === "string" && bodyMessage.trim().length > 0) {
          message = bodyMessage;
        }
      }
    } catch {
      try {
        const textFallback = await response.text();
        if (textFallback.trim().length > 0) {
          message = textFallback;
          parsedBody = textFallback;
        }
      } catch {
        // noop
      }
    }

    const error = new ApiError(message, response.status, parsedBody);

    // Attach parsed body as data for convenience
    (error as any).data = parsedBody;

    // Handle authentication errors specially
    if (error.isTokenError) {
      // Clear invalid token from localStorage/cookies
      if (typeof window !== 'undefined') {
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');

        // Notify auth error handler
        const event = new CustomEvent('api:auth-error', { detail: error });
        window.dispatchEvent(event);

        console.warn('Token inválido detectado. Redirecionando para login...');
      }
    }

    throw error;
  }

  return (await response.json()) as T;
}

export const apiRoutes = {
  collections: () => "/collections",
  search: () => "/search",
  auth: {
    login: () => "/auth/login",
    register: () => "/auth/register",
    refresh: () => "/auth/refresh",
    logout: () => "/auth/logout"
  },
  password: {
    forgot: () => "/auth/password/forgot",
    verify: () => "/auth/password/verify",
    reset: () => "/auth/password/reset"
  },
  user: {
    me: () => "/users/me",
    avatar: () => "/users/me/avatar",
    logo: () => "/users/me/logo",
    contact: () => "/users/me/contact",
    primaryColor: () => "/users/me/primary-color",
    refreshCrieOferta: () => "/users/me/refresh-crie-oferta"
  },
  crieOferta: {
    validarPreCadastro: () => "/crie-oferta/validar-precadastro",
    completarCadastro: () => "/crie-oferta/completar-cadastro"
  },
  videos: {
    templates: () => "/videos/templates",
    template: (slug: string) => `/videos/templates/${slug}`,
    personalization: () => "/videos/personalization",
    uploadLogo: () => "/videos/upload-logo",
    renders: () => "/videos/renders",
    render: (id: string) => `/videos/renders/${id}`,
    pix: () => "/videos/pix"
  },
  payments: {
    createPix: () => "/payments/mercadopago/create-pix",
    createStripeCheckout: () => "/payments/stripe/create-checkout",
    history: () => "/payments/history",
    eventStatus: (externalId: string) => `/payments/events/${externalId}`
  },
  quickEdit: {
    colorLogs: () => "/quick-edit/logs/colors"
  },
  downloads: {
    list: () => "/downloads",
    check: (artworkId: string) => `/downloads/check/${artworkId}`,
    editorCheck: (artworkId: string) => `/downloads/editor/check/${artworkId}`,
    record: () => "/downloads/record",
    timeUntilReset: () => "/downloads/time-until-reset"
  },
  editor: {
    createSession: () => "/editor/session",
    getSession: () => "/editor/session",
    consumeSession: () => "/editor/session/consume",
    deleteSession: () => "/editor/session"
  }
};

// Auth helper functions
export const authHelpers = {
  getToken: getStoredToken,

  setToken: setTokenInStorage,

  setRefreshToken: (refreshToken: string) => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('refreshToken', refreshToken);
      // Also set in cookie
      document.cookie = `refreshToken=${refreshToken}; path=/; max-age=${30 * 24 * 60 * 60}; SameSite=Lax`;
    }
  },

  clearTokens: () => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      // Also clear cookies
      document.cookie = 'authToken=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
      document.cookie = 'refreshToken=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    }
  },

  isAuthenticated: (): boolean => {
    return !!getStoredToken();
  },

  // Debug function to check token status
  debugToken: () => {
    if (typeof window === 'undefined') return null;

    const token = getStoredToken();
    const refreshToken = localStorage.getItem('refreshToken');

    // console.log('=== Token Debug ===');
    // console.log('Auth Token:', token ? `${token.substring(0, 20)}...` : 'null');
    // console.log('Refresh Token:', refreshToken ? `${refreshToken.substring(0, 20)}...` : 'null');
    // console.log('Is Authenticated:', !!token);

    if (token) {
      try {
        // Parse JWT payload (basic check without verification)
        const payload = JSON.parse(atob(token.split('.')[1]));
        // console.log('Token Payload:', payload);
        // console.log('Token Expires:', new Date(payload.exp * 1000));
        // console.log('Token Valid:', payload.exp * 1000 > Date.now());
      } catch (e) {
        // console.log('Token parsing error:', e);
      }
    }

    return { token, refreshToken };
  },

  logout: async () => {
    try {
      // Notify server about logout
      await apiFetch(apiRoutes.auth.logout(), { method: 'POST' });
    } catch (error) {
      console.error('Erro ao fazer logout no servidor:', error);
    } finally {
      authHelpers.clearTokens();
      // Redirect to login page
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
    }
  }
};
